import { BdGraphNode, BdGraph, BdGraphStateMap } from '../../../../lib/graph/graph.model';
import { Elections } from '../../../../lib/graph/elections';
import { ElectionResultModel } from '../../../../lib/graph/models/election-result.model';
import { merge, range, isEmpty } from 'lodash';

function formatElectionResult(
  property: string,
  id: string,
  result: boolean,
  dissenterIds: string[],
): BdGraphStateMap {
  const dissenters = dissenterIds.reduce((acc, dissenter) => {
    acc[dissenter] = { [property]: !result };
    return acc;
  }, {});

  dissenters[id] = { [property]: result };
  return dissenters;
}

function accumulateElectionResults(electionResults: ElectionResultModel[]): BdGraphStateMap {
  // a formatted election result is a BdGraphStateMap
  // this makes it easier to merge them with by node id instead of by property.
  return electionResults.reduce((acc, er) => {
    const properties = Object.keys(er);

    const formattedElectionResults = properties.map((property) => {
      return formatElectionResult(
        property,
        er[property].id,
        er[property].result,
        er[property].dissenters,
      );
    });

    const merged = formattedElectionResults.reduce((_acc, result) => {
      return merge(_acc, result);
    }, {});

    Object.keys(merged).forEach((nodeId) => {
      acc[nodeId] = merged[nodeId];
    });

    return acc;
  }, {} as BdGraphStateMap);
}

// In our pursuit of minimum synchronization, we must traverse the graph
// one last time and prune successor nodes that are the same as their predecessor,
// and thus redundant.
function pruneSyncGraph<TNode extends BdGraphNode>(
  syncGraph: BdGraphStateMap,
  successorIds: string[],
  properties: string[],
  graph: BdGraph<TNode>,
) {
  // collect props to prune
  const propsToPrune = successorIds.reduce(
    (acc: { successorId: string; prop: string }[], successorId) => {
      const predecessorId = graph.predecessors[successorId];
      const successor = syncGraph[successorId];
      const predecessor = syncGraph[predecessorId];

      for (const prop of properties) {
        if (successor[prop] === predecessor[prop]) {
          acc.push({ successorId, prop });
        }
      }
      return acc;
    },
    [],
  );

  // prune props, and the whole node if empty.
  for (const prop of propsToPrune) {
    delete syncGraph[prop.successorId][prop.prop];
    if (Object.keys(syncGraph[prop.successorId]).length === 0) {
      delete syncGraph[prop.successorId];
    }
  }

  return syncGraph;
}

// calculate inheritable graph state
export function calculateMiniminumSynchronization<TNode extends BdGraphNode>(
  graph: BdGraph<TNode>,
  syncProperties: Elections.SyncRules,
  leafStateMap: BdGraphStateMap,
): BdGraphStateMap {
  const properties: string[] = Object.keys(syncProperties);
  const initialVoterState = Elections.getInitialVoterState(leafStateMap, properties);
  const initialDepth = Elections.getInitialElectionDepth(leafStateMap, graph);

  const syncGraph = range(initialDepth, -1).reduce(
    (acc, depth) => {
      const elections = Elections.getEligibleElections(
        graph,
        Object.keys(acc.remainingVoters),
        depth,
      );

      const electionResults = Elections.getElectionResults(
        graph,
        acc.remainingVoters,
        elections,
        syncProperties,
      );

      const accumResults = accumulateElectionResults(electionResults.results);

      Object.keys(accumResults).forEach((key) => {
        acc.syncGraph[key] = accumResults[key];
      });

      acc.remainingVoters = electionResults.remainingVoters;
      return acc;
    },
    { syncGraph: {} as BdGraphStateMap, remainingVoters: initialVoterState },
  ).syncGraph;

  // At this point, syncGraph has all the correct property values for all nodes,
  // but it's not the minimum set.
  // see pruneSyncGraph

  const successorIds = Object.keys(syncGraph).filter((id) => {
    // is this id a root -> filter out
    if (graph.roots[id]) {
      return false;
    }

    // is this a successor? -> include
    if (graph.successors[id]) {
      return true;
    }

    return false;
  });

  const minSync = pruneSyncGraph(syncGraph, successorIds, properties, graph);

  return minSync;
}
